import url from 'url';

import { API_HOST, FETCH_FAIL_ERROR_MESSAGE, GENERIC_ERROR_MESSAGE } from '../constants';

export type JSONErrorResponse = {
  message?: string,
  error?: string,
  status?: number,
  statusText?: string,
  url?: string,
}

/**
 * @param {string} apiUrl
 * @param {object} fetchInit
 * @param {string} search
 * @param {boolean} noFormat Don't format apiUrl. Appropriate when apiUrl is already formatted.
 * @param {integer} timeoutMs Abort the request/response after timeoutMs milliseconds
 *
 * @return {Promise<Response>}
 */
export async function apiRequest(apiUrl: string, fetchInit: RequestInit = {}, search = '', noFormat = false, timeoutMs = 0): Promise<Response> {
  // This is a check for an env var defined in web/storybook/.storybook/main.js. We want to use http(not https) for api requests made from storybook.
  let storybookApiRequest = false;
  if (typeof process !== 'undefined' && process?.env?.STORYBOOK_HTTP_REQUESTS) {
    storybookApiRequest = true;
  }

  const uri = (noFormat || /^http/.test(apiUrl) || storybookApiRequest)
    ? apiUrl
    : url.format({
      protocol: 'https',
      host: API_HOST,
      pathname: apiUrl,
      search,
    });

  const init: RequestInit = {
    method: 'GET',
    mode: 'same-origin',
    credentials: 'same-origin',
    ...fetchInit,
    headers: {
      'Document-Location-Href': document.location.href,
      'X-Requested-With': 'fetch',
      ...fetchInit.headers,
    },
  };

  if (timeoutMs) {
    const controller = new AbortController();
    /* const timeoutId = */setTimeout(() => controller.abort(), timeoutMs);
    init.signal = controller.signal;
  }

  let response: Response;
  try {
    response = await fetch(uri, init);
  } catch (e) {
    // if fetch throws an error, it is most likely a network connection problem
    // throw a nicer error message, otherwise this just says 'Failed to fetch'
    // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#checking_that_the_fetch_was_successful
    throw new Error(FETCH_FAIL_ERROR_MESSAGE);
  }

  // handle server errors
  if (!response.ok) {
    if (/application\/json/.test(<string>response.headers.get('Content-Type'))) {
      const json = await response.json() as JSONErrorResponse;
      json.status = response.status;
      json.statusText = response.statusText;
      json.url = response.url;

      // set error message to server error
      if (!json.message) {
        json.message = json.error;
      }

      // add generic error message if one was not set or 5xx response status
      if (!json.message || (response.status > 499 && response.status < 600)) {
        json.message = GENERIC_ERROR_MESSAGE;
      }

      throw json;
    } else {
      throw new Error(GENERIC_ERROR_MESSAGE);
    }
  }

  return response;
}

export default apiRequest;
